import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { CheckCircle, Loader2, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuggestFeatureForm = ({ onFeatureAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    feature_name: '',
    feature_description: '',
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/features/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit');
      }

      setIsSuccess(true);
      setFormData({
        feature_name: '',
        feature_description: '',
        name: '',
        email: '',
      });
      
      // Notify parent to refresh the features list
      if (onFeatureAdded) {
        setTimeout(() => {
          onFeatureAdded();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
    setError('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-white text-[#B5541B] hover:bg-gray-100 font-semibold px-6 py-2 rounded-full">
          <Lightbulb className="w-4 h-4 mr-2" />
          Suggest a Feature
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {isSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-[#1F1E1E] mb-2">
              Thank you!
            </DialogTitle>
            <DialogDescription className="text-[#3B3535]/70">
              Your feature suggestion has been submitted. We'll review it and add it to our backlog.
              You've also been added to our waiting list!
            </DialogDescription>
            <Button
              onClick={handleClose}
              className="mt-6 bg-[#B5541B] hover:bg-[#8B3F14] text-white rounded-full"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1F1E1E] flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#B5541B]" />
                Suggest a Feature
              </DialogTitle>
              <DialogDescription className="text-[#3B3535]/70">
                Tell us what feature you'd like to see. Your suggestion will be reviewed and added to our backlog.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Feature Name */}
              <div className="space-y-2">
                <Label htmlFor="feature_name" className="text-[#3B3535] font-medium">
                  Feature Name <span className="text-[#B5541B]">*</span>
                </Label>
                <Input
                  id="feature_name"
                  type="text"
                  required
                  value={formData.feature_name}
                  onChange={(e) => setFormData({ ...formData, feature_name: e.target.value })}
                  placeholder="e.g., Voice Note Reporting"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>

              {/* Feature Description */}
              <div className="space-y-2">
                <Label htmlFor="feature_description" className="text-[#3B3535] font-medium">
                  Description <span className="text-[#B5541B]">*</span>
                </Label>
                <Textarea
                  id="feature_description"
                  required
                  value={formData.feature_description}
                  onChange={(e) => setFormData({ ...formData, feature_description: e.target.value })}
                  placeholder="Describe what this feature would do and why it would be helpful..."
                  rows={3}
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-sm text-[#3B3535]/60 mb-3">
                  Your details (so we can keep you updated)
                </p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="suggester_name" className="text-[#3B3535] font-medium">
                  Your Name <span className="text-[#B5541B]">*</span>
                </Label>
                <Input
                  id="suggester_name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="suggester_email" className="text-[#3B3535] font-medium">
                  Your Email <span className="text-[#B5541B]">*</span>
                </Label>
                <Input
                  id="suggester_email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !formData.feature_name || !formData.feature_description || !formData.name || !formData.email}
                className="w-full bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold py-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Suggestion'
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuggestFeatureForm;
